<template>
  <b-container fluid="xxl">
    <b-row class="align-items-center">
      <b-col cols="12" md="6">
        <h3>{{ $t('NAV_LINK_TEXT_ADMIN_PAGE_TAGS') }}</h3>
      </b-col>
      <b-col cols="12" md="6" class="text-md-right mt-3 mt-md-0">
        <b-button variant="cta" @click="openEditModal(null)">
          Neuer Page-Tag
        </b-button>
      </b-col>
    </b-row>

    <b-table
      striped
      hover
      small
      class="mt-4"
      :fields="fields"
      :items="pageTags"
      show-empty
    >
      <template #empty>
        <p class="w-100 text-center"><small>{{ $t('EMPTY_ITEMS_TABLE_MSG_PAGE_TAGS') }}</small></p>
      </template>

      <template #cell(col_tag)="{ item }">
        <div class="font-weight-bold">{{ item.page_tag }}</div>
      </template>

      <template #cell(col_active)="{ item }">
        <span v-if="Number(item.is_active) === 1" class="text-success">
          <font-awesome-icon :icon="['fa', 'smile']"/>
        </span>
        <span v-else class="text-danger">
          <font-awesome-icon :icon="['fa', 'skull']"/>
        </span>
      </template>

      <template #cell(col_groups)="{ item }">
        <div>
          <b-badge
            v-for="g in sortNames(extractGroupNames(item.groups))"
            :key="g"
            variant="light"
            class="mr-1 mb-1"
          >{{ g }}</b-badge>
        </div>
      </template>

      <template #cell(col_products)="{ item }">
        <div class="d-flex flex-column">
          <div
            v-for="pd in productDisplayList(item.products)"
            :key="pd.name"
            class="mb-1"
          >
            <b-badge v-if="pd.teaser !== ''" variant="primary" class="mr-1">{{ pd.teaser }}</b-badge>
            {{ pd.name }}
          </div>
        </div>
      </template>

      <template #cell(col_actions)="{ item }">
        <b-button size="sm" variant="light" @click="openEditModal(item.page_tag_id)" title="Editieren">
          <font-awesome-icon :icon="['fa', 'pen']"/>
        </b-button>
      </template>
    </b-table>

    <b-modal id="modal-edit-tag" v-model="showEdit" :title="modalTitle" @hidden="resetForm" hide-footer>
      <b-form @submit.prevent="onSubmit">
        <b-form-group label="Page-Tag" label-for="pt-tag" label-cols="4">
          <b-form-input id="pt-tag" v-model.trim="form.page_tag" required></b-form-input>
        </b-form-group>

        <b-form-group label="Aktivieren / deaktivieren" label-cols="4" label-class="d-flex align-items-center" content-class="d-flex align-items-center">
          <b-form-radio-group v-model="form.is_active" :options="activeOptions" name="is-active" :stacked="false" class="mb-0" buttons button-variant="outline-cta">
          </b-form-radio-group>
        </b-form-group>

        <b-form-group label="Page-Gruppen">
          <div>
            <b-form-checkbox
              v-for="pg in allPageGroups"
              :key="pg.page_group_id"
              v-model="selectedPageGroupIds"
              :value="Number(pg.page_group_id)"
              class="mb-2 big-checkbox"
            >
              <span class="d-inline-flex align-items-center">
                <span>{{ pg.page_group_title }}</span>
                <span v-if="Number(pg.is_active) === 0" class="text-danger ml-2" title="Inaktiv">
                  <font-awesome-icon :icon="['fa', 'skull']"/>
                </span>
              </span>
            </b-form-checkbox>
          </div>
        </b-form-group>

        <b-form-group label="Produkte">
          <div>
            <b-form-checkbox
              v-for="pr in allProducts"
              :key="pr.product_id"
              v-model="selectedProductIds"
              :value="Number(pr.product_id)"
              class="mb-2 big-checkbox"
            >
              <span class="d-inline-flex align-items-center">
                <span>
                  <b-badge v-if="(pr.teaser || '').trim() !== ''" variant="light" class="mr-1">{{ pr.teaser }}</b-badge>
                  {{ pr.product_name }}
                </span>
                <span v-if="Number(pr.product_is_active) === 0" class="text-danger ml-2" title="Inaktiv">
                  <font-awesome-icon :icon="['fa', 'skull']"/>
                </span>
              </span>
            </b-form-checkbox>
          </div>
        </b-form-group>

        <div class="d-flex justify-content-end">
          <b-button variant="secondary" class="mr-2" @click="showEdit=false">Abbrechen</b-button>
          <b-button type="submit" variant="cta" :disabled="submitting">Speichern</b-button>
        </div>
      </b-form>
    </b-modal>

    <b-modal id="modal-result-tag" v-model="showResult" :title="resultTitle" ok-only ok-variant="cta" @ok="afterResultOk">
      <div v-html="resultMessage"></div>
    </b-modal>
  </b-container>
</template>

<script>
import axios from 'axios'

export default {
  name: 'areaAdminPageTags',
  data() {
    return {
      pageTags: [],
      fields: [
        { key: 'col_tag', label: 'Page-Tag' },
        { key: 'col_active', label: 'Status', class: 'text-center' },
        { key: 'col_groups', label: 'Page-Gruppen' },
        { key: 'col_products', label: 'Produkte' },
        { key: 'col_actions', label: '', class: 'text-right' }
      ],
      showEdit: false,
      submitting: false,
      form: {
        page_tag_id: null,
        page_tag: '',
        is_active: '0'
      },
      activeOptions: [
        { text: 'inaktiv', value: '0' },
        { text: 'aktiv', value: '1' }
      ],
      // selectable lists
      allPageGroups: [],
      allProducts: [],
      selectedPageGroupIds: [],
      selectedProductIds: [],

      showResult: false,
      resultTitle: '',
      resultMessage: '',
    }
  },
  computed: {
    modalTitle() {
      return this.form.page_tag_id ? 'Page-Tag bearbeiten' : 'Neuer Page-Tag';
    }
  },
  methods: {
    async fetchPageTags() {
      try {
        const response = await axios.post(process.env.VUE_APP_API, { type: 'get_page_tags', cookie: this.$store.getters.getToken });
        this.pageTags = Array.isArray(response.data) ? response.data : [];
      } catch (e) {
        this.pageTags = [];
      }
    },
    async fetchSelectableData() {
      try {
        const [groupsRes, productsRes] = await Promise.all([
          axios.post(process.env.VUE_APP_API, { type: 'get_page_groups', cookie: this.$store.getters.getToken }),
          axios.post(process.env.VUE_APP_API, { type: 'get_productlist', cookie: this.$store.getters.getToken })
        ]);
        this.allPageGroups = Array.isArray(groupsRes.data) ? groupsRes.data : [];
        // Some backends return {product_id, product_name} in different keys; try both products/product
        const pr = Array.isArray(productsRes.data) ? productsRes.data : (Array.isArray(productsRes.data?.products) ? productsRes.data.products : []);
        this.allProducts = pr;
      } catch (e) {
        this.allPageGroups = [];
        this.allProducts = [];
      }
    },
    sortNames(arr) {
      return (arr || []).map(s => String(s)).sort((a,b)=>a.localeCompare(b, 'de', { sensitivity: 'base' }));
    },
    extractGroupNames(tags) {
      if (!tags) return [];
      if (Array.isArray(tags)) {
        return tags.map(t => t?.page_group_title || t?.page_group || t).filter(Boolean);
      }
      return String(tags).split(',').map(s=>s.trim()).filter(Boolean);
    },
    extractProductNames(products) {
      if (!products) return [];
      if (Array.isArray(products)) {
        return products.map(p => p?.product_name || p?.name || p).filter(Boolean);
      }
      return String(products).split(',').map(s=>s.trim()).filter(Boolean);
    },
    productDisplayList(products) {
      const list = [];
      if (!products) return list;
      if (Array.isArray(products)) {
        for (const p of products) {
          const name = (p && (p.product_name || p.name)) ? String(p.product_name || p.name) : (p ? String(p) : '');
          const teaser = (p && p.teaser ? String(p.teaser) : '').trim();
          if (name) {
            list.push({ name, teaser });
          }
        }
      } else {
        const names = String(products).split(',').map(s => s.trim()).filter(Boolean);
        for (const name of names) {
          list.push({ name, teaser: '' });
        }
      }
      return list.sort((a,b)=>a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }));
    },
    extractSelectedGroupIds(tags) {
      if (!tags) return [];
      if (Array.isArray(tags)) {
        return tags.map(t => Number(t.page_group_id || t.id)).filter(n => !isNaN(n));
      }
      return [];
    },
    extractSelectedProductIds(products) {
      if (!products) return [];
      if (Array.isArray(products)) {
        return products.map(p => Number(p.product_id || p.id)).filter(n => !isNaN(n));
      }
      return [];
    },
    async openEditModal(page_tag_id) {
      this.resetForm();
      await this.fetchSelectableData();
      if (page_tag_id) {
        try {
          const response = await axios.post(process.env.VUE_APP_API, { type: 'get_page_tag', page_tag_id, cookie: this.$store.getters.getToken });
          const pt = Array.isArray(response.data) ? response.data[0] : response.data;
          if (pt) {
            this.form = {
              page_tag_id: Number(pt.page_tag_id) || null,
              page_tag: pt.page_tag || '',
              is_active: String(pt.is_active) === '1' ? '1' : '0'
            };
            this.selectedPageGroupIds = this.extractSelectedGroupIds(pt.tags);
            this.selectedProductIds = this.extractSelectedProductIds(pt.products);
          }
        } catch (e) {
          // ignore
        }
      }
      this.showEdit = true;
    },
    resetForm() {
      this.form = {
        page_tag_id: null,
        page_tag: '',
        is_active: '0'
      };
      this.selectedPageGroupIds = [];
      this.selectedProductIds = [];
      this.submitting = false;
    },
    validateForm() {
      return this.form.page_tag.trim() !== '';
    },
    async onSubmit() {
      if (!this.validateForm()) {
        this.resultTitle = this.$t('ERROR_TITLE_DEFAULT');
        this.resultMessage = 'Bitte fülle alle erforderlichen Felder aus.';
        this.showResult = true;
        return;
      }
      this.submitting = true;
      try {
        const payload = {
          type: 'page_tag_edit',
          data: {
            page_tag_id: this.form.page_tag_id,
            page_tag: this.form.page_tag,
            is_active: Number(this.form.is_active),
            page_groups: this.selectedPageGroupIds,
            page_products: this.selectedProductIds,
          },
          cookie: this.$store.getters.getToken
        };
        const response = await axios.post(process.env.VUE_APP_API, payload, { withCredentials: true });
        const ok = response && response.data && Number(response.data.page_tag_id || response.data) > 0;
        this.resultTitle = ok ? this.$t('SUCCESS_TITLE_DEFAULT') : this.$t('ERROR_TITLE_DEFAULT');
        this.resultMessage = ok ? this.$t('SUCCESS_TEXT_SAVE_DATA') : (response.data && response.data.error ? response.data.error : 'Unbekannter Fehler');
        this.showResult = true;
        if (ok) {
          this.showEdit = false;
          await this.fetchPageTags();
        }
      } catch (e) {
        this.resultTitle = this.$t('ERROR_TITLE_DEFAULT');
        this.resultMessage = e?.message || 'Fehler beim Speichern';
        this.showResult = true;
      } finally {
        this.submitting = false;
      }
    },
    afterResultOk() {
      this.showResult = false;
    }
  },
  mounted() {
    this.fetchPageTags();
  }
}
</script>

<style scoped>
/* Selected radio (outline-cta) appears filled */
.btn-outline-cta.active,
.btn-outline-cta:active,
.btn-outline-cta.active:focus,
.btn-outline-cta:focus,
.btn-outline-cta:hover {
  background-color: var(--cta, #007bff);
  border-color: var(--cta, #007bff);
  color: #fff !important;
}

/* Keep unselected outline buttons with proper text color */
.btn-outline-cta {
  color: var(--cta, #007bff);
}

/* Bigger, more visible checkboxes in the edit modal */
::v-deep .big-checkbox.custom-control .custom-control-label::before,
::v-deep .big-checkbox.custom-control .custom-control-label::after {
  width: 1.25rem;
  height: 1.25rem;
  top: 0.1rem;
}
::v-deep .big-checkbox.custom-control .custom-control-label {
  padding-left: 2rem;
}
::v-deep .big-checkbox.custom-control .custom-control-input:checked ~ .custom-control-label::after {
  background-size: 70% 70%;
}
</style>
